import { useState, useEffect } from "react";
import QuoteCarousel from "@/components/QuoteCarousel";
import { Loader2, Building2, CheckCircle, AlertTriangle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogoMark } from "@/components/Logo";
import { useToast } from "@/hooks/use-toast";

interface InviteInfo {
  creditorName: string;
  creditor: string;
  debtId: number;
  balance: number;
  verificationStatus: "pending" | "verified" | "failed";
  alreadySubmitted: boolean;
}

export default function CreditorPortal() {
  const { toast } = useToast();

  const token = new URLSearchParams(window.location.search).get("token") ?? "";

  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Banking form state
  const [legalName, setLegalName] = useState("");
  const [routingNumber, setRoutingNumber] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountType, setAccountType] = useState<"company" | "individual">("company");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError("No invite token found in this link. Please use the exact link from your email.");
      setLoading(false);
      return;
    }

    fetch(`/api/creditor-portal/invite?token=${encodeURIComponent(token)}`, {
      credentials: "include",
    })
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json() as { error: string };
          throw new Error(data.error ?? "Invalid link");
        }
        return res.json() as Promise<InviteInfo>;
      })
      .then((data) => {
        setInviteInfo(data);
        setLegalName(data.creditorName);
        if (data.alreadySubmitted || data.verificationStatus === "verified") {
          setSubmitted(true);
        }
      })
      .catch((err: Error) => {
        setLoadError(err.message);
      })
      .finally(() => setLoading(false));
  }, [token]);

  async function handleBankingSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!legalName.trim() || !routingNumber.trim() || !accountNumber.trim()) return;

    setSubmitting(true);
    try {
      const res = await fetch("/api/creditor-portal/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token, legalName: legalName.trim(), routingNumber, accountNumber, accountType }),
      });

      const data = await res.json() as { success?: boolean; message?: string; error?: string; verifyUrl?: string };

      if (!res.ok) {
        toast({ title: "Submission failed", description: data.error ?? "Please try again.", variant: "destructive" });
        return;
      }

      setSubmitted(true);
      toast({ title: "Banking details submitted!", description: data.message });
    } catch {
      toast({ title: "Network error", description: "Could not submit your details. Please check your connection.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-background relative overflow-hidden">
      <img
        src="https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=1600&q=80&fit=crop&auto=format"
        alt="" aria-hidden loading="lazy"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.13, filter: "grayscale(0.15)", pointerEvents: "none" }}
      />
      <div className="max-w-lg mx-auto px-4 py-12">
        <div className="flex items-center gap-3 mb-8">
          <LogoMark size={32} />
          <div>
            <span className="font-bold text-lg">DebtBridge Foundation</span>
            <p className="text-xs text-muted-foreground">Creditor Payment Portal</p>
          </div>
        </div>

        {loadError ? (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800">Link not valid</p>
                  <p className="text-sm text-red-700 mt-1">{loadError}</p>
                  <p className="text-sm text-red-600 mt-2">Please use the exact link from the email sent to you. If the link has expired, contact DebtBridge at support@debtbridge.org.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {inviteInfo?.verificationStatus === "verified" ? (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-3">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                    <p className="font-semibold text-green-800 text-lg">Bank account verified!</p>
                  </div>
                  <p className="text-green-700">Your ACH banking details have been successfully verified. DebtBridge will schedule your payment shortly.</p>
                  <p className="text-sm text-green-600 mt-3">Debt Reference: #{String(inviteInfo.debtId).padStart(8, "0")}</p>
                </CardContent>
              </Card>
            ) : submitted ? (
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-3">
                    <CheckCircle className="h-6 w-6 text-blue-600" />
                    <p className="font-semibold text-blue-800 text-lg">Details received!</p>
                  </div>
                  <p className="text-blue-700 text-sm">Your banking details have been submitted. Stripe will send two small test deposits (each less than $1) to your account within 1–2 business days.</p>
                  <p className="text-blue-700 text-sm mt-2">Once you see those deposits, return to confirm the amounts and complete verification:</p>
                  <a
                    href={`/creditor-portal/verify?token=${encodeURIComponent(token)}`}
                    className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
                  >
                    Confirm Deposit Amounts <ArrowRight className="h-4 w-4" />
                  </a>
                </CardContent>
              </Card>
            ) : (
              <>
                <Card className="mb-6">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Building2 className="h-5 w-5 text-blue-600" />
                      Payment Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Creditor</span>
                      <span className="font-medium">{inviteInfo?.creditor}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Amount to receive</span>
                      <span className="font-bold text-green-700">
                        ${(inviteInfo?.balance ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Debt Reference</span>
                      <span className="font-mono text-xs">#{String(inviteInfo?.debtId).padStart(8, "0")}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Submit ACH Banking Details</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Stripe will verify your account by sending two small test deposits (under $1 each). You'll confirm those amounts to complete verification.
                    </p>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleBankingSubmit} className="space-y-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="legalName">Legal Business or Individual Name</Label>
                        <Input
                          id="legalName"
                          value={legalName}
                          onChange={(e) => setLegalName(e.target.value)}
                          placeholder="As it appears on your bank account"
                          required
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="accountType">Account Holder Type</Label>
                        <select
                          id="accountType"
                          value={accountType}
                          onChange={(e) => setAccountType(e.target.value as "company" | "individual")}
                          className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                        >
                          <option value="company">Business / Company</option>
                          <option value="individual">Individual</option>
                        </select>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="routingNumber">Routing Number</Label>
                        <Input
                          id="routingNumber"
                          value={routingNumber}
                          onChange={(e) => setRoutingNumber(e.target.value.replace(/\D/g, ""))}
                          placeholder="9-digit routing number"
                          maxLength={9}
                          required
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="accountNumber">Account Number</Label>
                        <Input
                          id="accountNumber"
                          value={accountNumber}
                          onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ""))}
                          placeholder="Checking or savings account number"
                          required
                        />
                      </div>

                      <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                        <strong>US bank accounts only.</strong> Your routing and account numbers are transmitted securely to Stripe and are never stored by DebtBridge. Only you and Stripe will have access to these details.
                      </div>

                      <Button type="submit" className="w-full" disabled={submitting}>
                        {submitting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Submitting...</> : "Submit Banking Details"}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}

        <p className="text-xs text-center text-muted-foreground mt-8">
          Questions? Email <a href="mailto:support@debtbridge.org" className="underline">support@debtbridge.org</a>
        </p>
      </div>
      <QuoteCarousel />
    </div>
  );
}
